import { ManagedServiceId } from '@axinom/mosaic-id-utils';
import {
  Accordion,
  AccordionItem,
  EmptyStation,
  MessageBar,
} from '@axinom/mosaic-ui';
import clsx from 'clsx';
import { config } from 'config';
import type { BreadcrumbResolver } from 'models/breadcrumb';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  ObjectStorageLogsDocument,
  ObjectStorageLogsOrderBy,
  type ObjectStorageLogsQuery,
} from '../../../../generated/hosting-service';
import { getManagedServiceAvailabilityMessage } from '../../../../util/managedServiceUtil';
import { environmentApolloClient } from '../../../environmentApolloClient';
import classes from './StorageAccountLogs.module.scss';

export const StorageAccountLogs: React.FC = () => {
  const { envId } = useParams<{
    envId: string;
  }>();

  const [queryResult, setQueryResult] = useState<ObjectStorageLogsQuery>();
  const [serviceStatusMessage, setServiceStatusMessage] = useState<
    string | null
  >(null);

  useEffect(() => {
    (async () => {
      // Check the status of hosting-service
      const statusMessage = await getManagedServiceAvailabilityMessage(
        ManagedServiceId.HOSTING_SERVICE,
        envId,
      );

      if (statusMessage !== null) {
        setServiceStatusMessage(statusMessage);
      } else {
        const envApolloClient = environmentApolloClient(
          envId,
          new URL(`/graphql`, config.hostingServiceAdminBaseUrl).toString(),
        );

        const { data } = await envApolloClient.query<ObjectStorageLogsQuery>({
          query: ObjectStorageLogsDocument,
          variables: {
            condition: {
              environmentId: envId,
            },
            orderBy: [ObjectStorageLogsOrderBy.TimestampDesc],
          },
          fetchPolicy: 'network-only',
        });

        setQueryResult(data);
      }
    })();
  }, [envId]);

  const dateFormatter: Intl.DateTimeFormat = new Intl.DateTimeFormat(
    navigator.language,
    {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
    },
  );

  return (
    <EmptyStation title={`Storage Account Logs`} subtitle={''}>
      <>
        {serviceStatusMessage ? (
          <MessageBar type="warning" title={serviceStatusMessage}></MessageBar>
        ) : (
          <Accordion
            header={
              <div className={clsx(classes.row, classes.header)}>
                <span>Timestamp</span>
                <span>Message Type</span>
              </div>
            }
            stickyRows={true}
          >
            {queryResult?.objectStorageLogs?.nodes.map((node) => (
              <AccordionItem
                key={node.id}
                header={
                  <div className={clsx(classes.row)}>
                    <span>
                      {dateFormatter.format(new Date(node.timestamp))}
                    </span>
                    <span>{node.messageType}</span>
                  </div>
                }
              >
                <div className={clsx(classes.content)}>
                  <label>Message Body</label>
                  <pre>{JSON.stringify(node.message, undefined, 2)}</pre>
                </div>
              </AccordionItem>
            ))}
          </Accordion>
        )}
      </>
    </EmptyStation>
  );
};

export const StorageAccountLogsCrumb: BreadcrumbResolver = () =>
  'Storage Account Logs';
